import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import { CalendarUtils, DateData } from 'react-native-calendars';
import { useWindowDimensions } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { ADD_LOCAL_CALENDARS, DATE_SELECTED } from '@reduxLocal/actions/types';
import CalendarComponent, { CalendarHandle } from './Components/CalendarComponent';
import CalendarSelectionModal from './Components/calendarSelectionModal';
import CalendarSelectionList from './Components/CalendarSelectionList';
import AddGoogleCal from './Components/addGoogleCal';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import EventDetailsSlider, { EventDetailsSliderRef } from './Components/EventDetailsSlider';
import apis from '@api/api';
import getId from '@api/getId';
import { ToolbarContext } from '@hooks/toolbarContext';
import handleEventList from './utils/handleEventList';
import { DefaultRootState } from '@reduxLocal/persistState';
import { YStack, Text, XStack, View, Separator, Label, SizableText, Checkbox, H5 } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';


const today = new Date();
const CalendarScreen = () => {
  const selectedDate = useSelector((state: DefaultRootState) => state.calendar.dateSelected);
  const { setCalendarHeader } = useContext(ToolbarContext);
  const dispatch = useDispatch();
  const [calendarHeight, setCalendarHeight] = useState(0);
  const [showAI, setShowAI] = useState(true);
  const { height } = useWindowDimensions();
  
  useEffect(() => {
    //If calendar does not take the entire height of the screen, show the AI helper
    if (calendarHeight > 0 && height - calendarHeight > 400) {
      console.log('show AI');
      setShowAI(true);
    } else {
      setShowAI(false);
    }
  }, [height, calendarHeight])


  // Refs and State
  const theme = themeSelector();
  const calendarRef = useRef<CalendarHandle>(null);
  const robotRef = useRef<RobotHandle>(null);
  const eventDetailsRef = useRef<EventDetailsSliderRef>(null);
  const calendarImportRef = useRef(null);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  // ✅ FIX: Use local state instead of Redux to avoid stale data
  const [localCalendars, setLocalCalendarsState] = useState<any[]>([]);
  const [calendarType, setCalendarType] = useState('all');
  const [googleCalVisible, setGoogleCalVisible] = useState(false);
  const [eventList, setEventList] = useState([]);
  const [finalMarked, setFinalMarked] = useState({});
  const [calHeight, setCalHeight] = useState(0);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [month, setMonth] = useState<DateData>({
    year: moment(today).year(),
    month: moment(today).month(),
    day: moment(today).day(),
    timestamp: today.getTime(),
    dateString: CalendarUtils.getCalendarDateString(today),
  });

  // Fetch calendars and events
  const fetchCalendarsAndEvents = async () => {
    try {
      dispatch({ type: "CALENDAR_LOADING" })
      const [calendarsResponse, calendarContentResponse] = await Promise.all([
        apis.getCalendars(),
        apis.getCalendarContent(month),
      ]);


      // ✅ FIX: API already extracts .data, so we only need one more level
      const calendars = calendarsResponse?.data || [];  // Changed from calendarsResponse?.data?.data
      const events = calendarContentResponse?.data?.data || [];


      // ✅ FIX: Calendars are now returned directly, not nested in item.calendar
      const localCalendars = calendars; // Calendars are already in the correct format

      // Auto-select all calendars on first load
      const calendarIds = calendars.map(cal => {
        const id = cal._id || cal.calendarId || '';
        return id;
      }).filter(Boolean);

      // Set calendars and selected calendars first
      setLocalCalendarsState(calendars);
      dispatch({ type: ADD_LOCAL_CALENDARS, payload: calendars });

      // Only auto-select if no calendars are currently selected
      if (selectedCalendars.length === 0 && calendarIds.length > 0) {
        setSelectedCalendars(calendarIds);
      }

      // Store events for later filtering
      setEventList(events);

      // Process events immediately with all calendars (on first load) or selected calendars
      const calendarsToUse = selectedCalendars.length === 0 ? calendarIds : selectedCalendars;

      try {
        // Pass all calendars for processing - handleEventList will show all events
        const markedEvents = await handleEventList(events, calendars, calendarType);
        setFinalMarked(markedEvents);
        dispatch({ type: "CALENDAR_STOPPED_LOADING" });

        // Mark initial load as complete
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }

        // Always return a value - never undefined
        return { calendars, events, markedEvents, localCalendars };
      } catch (error) {
        console.error('Error handling local calendar:', error);
        dispatch({ type: "CALENDAR_STOPPED_LOADING" });
        // Return empty data structure instead of undefined
        return { calendars: [], events: [], markedEvents: {}, localCalendars: [] };
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      dispatch({ type: "CALENDAR_STOPPED_LOADING" });
      // Always return a value - never undefined
      return { calendars: [], events: [], markedEvents: {}, localCalendars: [] };
    }
  }

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ['calendarData', month.year, month.month],
    queryFn: fetchCalendarsAndEvents,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });

  const setLocalCalendars = useCallback((calendars) => {

    // ✅ FIX: Store in local state instead of Redux
    setLocalCalendarsState(calendars);

    // Also dispatch to Redux for backward compatibility
    dispatch({ type: ADD_LOCAL_CALENDARS, payload: calendars });
  }, [dispatch]);

  const _handleLocalCalendar = useCallback(
    async (events, calendars) => {
      setEventList(events);
      try {
        // Filter calendars and events based on selected calendars
        // If no calendars are selected, show all
        const filteredCalendars = selectedCalendars.length === 0
          ? calendars
          : calendars.filter(cal => {
            const calendarId = cal._id || cal.calendarId || '';
            return selectedCalendars.includes(calendarId);
          });

        // IMPORTANT: Also filter events to only show events from selected calendars
        const filteredEvents = selectedCalendars.length === 0
          ? events
          : events.filter(event => {
            const eventCalendarId = event.calendarId || event.calendar?._id || event.calendar?.calendarId || '';
            return selectedCalendars.includes(eventCalendarId);
          });

        const markedEvents = await handleEventList(filteredEvents, filteredCalendars, calendarType);
        setFinalMarked(markedEvents);
        dispatch({ type: "CALENDAR_STOPPED_LOADING" });
      } catch (error) {
        console.error('Error handling local calendar:', error);
      }
    },
    [calendarType, selectedCalendars, dispatch]
  );

  const handleMarkSelection = useCallback(
    (date) => {
      const listEvents = Object.entries(finalMarked)
        .filter(([key]) => moment(key).isSame(moment(date), 'day'))
        .map(([, value]) => value);

      dispatch({ type: DATE_SELECTED, payload: { date, events: listEvents } });
    },
    [finalMarked, dispatch]
  );

  const handleEventClick = useCallback((event: any) => {
    console.log('Event clicked:', event);
    console.log('Event details ref:', eventDetailsRef.current);
    if (eventDetailsRef.current) {
      eventDetailsRef.current.showEvent(event);
    } else {
      console.warn('Event details ref is null');
    }
  }, []);

  // Calendar selection handlers
  const handleCalendarToggle = useCallback((calendarId: string) => {
    setSelectedCalendars(prev => {
      if (prev.includes(calendarId)) {
        return prev.filter(id => id !== calendarId);
      } else {
        return [...prev, calendarId];
      }
    });
  }, []);

  const handleSelectAllCalendars = useCallback(() => {
    if (localCalendars && localCalendars.length > 0) {
      const allCalendarIds = localCalendars.map(cal => cal._id || cal.calendarId || '').filter(Boolean);
      setSelectedCalendars(allCalendarIds);
    }
  }, [localCalendars]);

  const handleDeselectAllCalendars = useCallback(() => {
    setSelectedCalendars([]);
  }, []);

  // Update calendar display when selected calendars change
  useEffect(() => {
    // Skip on initial load - let fetchCalendarsAndEvents handle it
    if (isInitialLoad) {
      return;
    }

    // Only re-filter if we have calendars and events, and we're not currently loading
    if (localCalendars && localCalendars.length > 0 && eventList.length > 0 && !isLoading && !isFetching) {
      _handleLocalCalendar(eventList, localCalendars);
    }
  }, [selectedCalendars, calendarType, isInitialLoad, localCalendars, eventList, isLoading, isFetching, _handleLocalCalendar]);

  // Set calendar header ref
  useEffect(() => {
    setCalendarHeader(calendarRef.current);
  }, [setCalendarHeader]);

  // Render
  return (
    <DefaultScreen
      screenTitle="Calendar"
      scrollDisabled
      containerStyle={{
        paddingLeft: 30,
        paddingBottom: 50,
        paddingRight: 0,
        flexDirection: 'column-reverse',
      }}
      noMargin
      layoutCallback={({ height }) => {
        setCalHeight(height);
      }}
    >
      <CalendarSelectionModal
        ref={calendarImportRef}
        calendars={localCalendars.length > 0 ? localCalendars as any : [{}] as [Object]}
        calendarSelectionCallback={(value) => setLocalCalendars(value)}
      />
      <AddGoogleCal visible={googleCalVisible} setVisible={setGoogleCalVisible} setCalendars={() => { }} />
      {/* Calendar Selection List */}
      <CalendarSelectionList
        calendars={localCalendars || []}
        selectedCalendars={selectedCalendars}
        onCalendarToggle={handleCalendarToggle}
        onSelectAll={handleSelectAllCalendars}
        onDeselectAll={handleDeselectAllCalendars}
        isLoading={isLoading || isFetching}
      />
      <XStack flexWrap="wrap" paddingHorizontal={"3vw"} flex={1}>
        <YStack
          style={{
            flex: 3,
            top: -90,
            minWidth: 600,
            maxWidth: 1200,
            borderRadius: 24,
            padding: 20,
            margin: 16,
            marginTop:16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: theme ? 0.15 : 0.25,
            shadowRadius: 16,
            elevation: 10,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
          }}
        >
          <CalendarComponent
            googleCal={() => setGoogleCalVisible(true)}
            calendarType={calendarType}
            dateCallback={(date: DateData) => {
              setMonth(date);
            }}
            setMonth={(date: string) => {
              // Handle month change if needed
            }}
            ref={calendarRef}
            calHeight={calHeight}
            dateSelected={selectedDate}
            setCalendarType={setCalendarType}
            handleMarkSelection={handleMarkSelection}
            onEventClick={handleEventClick}
            eventList={eventList}
            finalMarked={finalMarked}
            calLoading={isLoading || isFetching}
          />
        </YStack>
        {/* <AIRemindersComponent
          eventList={eventList}
          selectedDate={selectedDate}
        /> */}
      </XStack>
      <EventDetailsSlider
        ref={eventDetailsRef}
        onEventUpdated={refetch}
        onEventDeleted={refetch}
      />
    </DefaultScreen>
  );
};

export default CalendarScreen;
